import React, { useState } from 'react';

import classNames from 'classnames';

import { useFeatureFlag } from 'wanderu-analytics';
import {
    Form,
    FormError,
    FormInput,
} from 'wanderu-component-form';
import { WDSButton } from 'wanderu-components';

import { clickEvent } from './events';

import styles from './DiscountForm.scss';

const wait = () => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(null);
        }, 500);
    });
};

export interface EventProps {
    discount_code_entered?: string
    booking_session_id: string
    is_one_way: boolean
    itinerary_id: string
    order_id: string
    pax_count: number
    trips?: {
        direction: string
        carrier_code: string
        mode: string
        departure_time: string
        departure_station_code: string
        arrival_time: string
        arrival_station_code: string
        duration_in_minutes: number
        is_routed: boolean
        transfer_count: number
    }[]
}

interface DiscountFormProps {
    eventProps: EventProps
    isTripSelection?: boolean
}

const DiscountForm = (props: DiscountFormProps) => {
    const {
        eventProps,
        isTripSelection = false,
    } = props;

    const [isError, setIsError] = useState(false);
    const [fieldText, setFieldText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.nativeEvent?.type === 'click' && !event.target.value) {
            setIsError(false);
            clickEvent('Discount Code Cleared', eventProps);
        }

        setFieldText(event.target.value);
    };

    const onSubmit = async () => {
        clickEvent('Discount Code Submitted', {
            discount_code_entered: fieldText,
            ...eventProps,
        });
        setIsError(false);
        setIsProcessing(true);
        await wait();
        setIsProcessing(false);
        setIsError(true);
    };

    const { isEnabled } = useFeatureFlag('checkout-discount-code-box', false, 'checkout-code');

    if (!isEnabled) {
        return null;
    }

    return (
        <>
            <Form onSubmit={onSubmit}>
                <fieldset className={classNames(
                    styles.fieldset,
                    {
                        [styles.tripSelection]: isTripSelection,
                    },
                )}>
                    <FormInput
                        className={styles.field}
                        clearable={false}
                        disabled={isProcessing}
                        labelClassName={styles.label}
                        labelFocusedClassName={styles.labelFocused}
                        onChange={handleOnChange}
                        placeholder="Discount code or gift card"
                        showValidity={isError}
                        valid={!isError}
                        value={fieldText}
                    />
                    <WDSButton
                        className={styles.button}
                        classes={{
                            disabled: styles.disabled,
                        }}
                        color="neutral"
                        disabled={isProcessing}
                        size="small"
                        type="submit"
                        variant="unstyled"
                    >
                        Apply
                    </WDSButton>
                </fieldset>
                {isError && (
                    <FormError className={styles.formError}>
                        This is not a valid code.
                    </FormError>
                )}
            </Form>
            <hr className={styles.hr} />
        </>
    );
};

export default DiscountForm;
